/**
 * New Board modal — pick a template + name, or start blank.
 *
 * Templates come from GET /api/boards/templates so the frontend never
 * duplicates the list. Chosen template + name → POST /api/boards.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Layers, TrendingUp, ListChecks, Calendar as CalendarIcon, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const ICON_FOR = {
  blank:   Layers,
  sales:   TrendingUp,
  project: Sparkles,
  todo:    ListChecks,
  content: CalendarIcon,
};

const NewBoardModal = ({ onClose, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState('');
  const [picked, setPicked] = useState('blank');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/boards/templates`, { credentials: 'include' });
        if (r.ok) setTemplates(await r.json());
      } catch (e) { toast.error('Could not load templates'); }
      finally { setLoading(false); }
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('Give your board a name'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/boards`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), template: picked }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      const board = await r.json();
      toast.success(`${board.name} ready`);
      onCreated?.(board);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="new-board-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="font-bold text-lg flex items-center gap-2"><Layers className="w-5 h-5" /> New Board</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <Label htmlFor="brd-name">Board name <span className="text-rose-600">*</span></Label>
            <Input
              id="brd-name"
              data-testid="brd-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q1 Sales Pipeline"
              className="mt-1"
              autoFocus
              required
            />
          </div>
          <div>
            <Label>Start from a template</Label>
            {loading ? (
              <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
            ) : (
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {templates.map((t) => {
                  const Icon = ICON_FOR[t.key] || Layers;
                  const active = picked === t.key;
                  return (
                    <button
                      type="button"
                      key={t.key}
                      onClick={() => setPicked(t.key)}
                      data-testid={`brd-tmpl-${t.key}`}
                      className={`text-left p-3 rounded-lg border transition ${
                        active
                          ? 'ring-2 ring-slate-400 bg-slate-50 border-slate-300'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-slate-600" />
                        <span className="font-semibold text-sm">{t.label}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">{t.description}</p>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {t.columns.length} column{t.columns.length === 1 ? '' : 's'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="brd-submit" className="bg-slate-900 hover:bg-slate-800">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating…</> : 'Create Board'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewBoardModal;
